import {
  Mountain,
  Wrench,
  ClipboardCheck,
  AlertTriangle,
  Layers,
  ArrowRight,
} from "lucide-react";
import {
  Nav,
  Footer,
  PageHero,
  CtaBlock,
  TrustStrip,
  usePageSeo,
  AGG_SOIL_SCREENING_URL,
  AGG_MOBILE_CRUSHING_URL,
  AGG_TRACK_OUTCOME_URL,
} from "../layout/shared";

const WE_CURRENTLY_ASSESS = [
  {
    icon: Mountain,
    t: "Soil and stone route assessment",
    d: "An evidence-led first review of soil, stone or hardcore leaving a site: what it is, where it came from, what analysis exists and which authorised routes might apply.",
    img: AGG_SOIL_SCREENING_URL,
  },
  {
    icon: Wrench,
    t: "Mobile-project feasibility reviews",
    d: "Feasibility for proposed mobile crushing, screening or blending work at a customer site: what regulatory route applies and what evidence and approvals must be in place.",
    img: AGG_MOBILE_CRUSHING_URL,
  },
];

const INFO_TO_SEND = [
  "Site postcode and access route.",
  "Origin of the material and how it was produced.",
  "Rough tonnages, dimensions and physical description.",
  "Any waste-classification, WM3 or laboratory analysis you already hold.",
  "Any drawings, method statements or specifications for a proposed project.",
  "Your target start date and any planning or contract constraints.",
];

const NOT_YET_ADVERTISED = [
  "Own-site receiving of soil, stone or hardcore at Eardiston",
  "An operated mobile crushing and screening service",
  "Recycled aggregate stock, sales or supply",
  "Tipping, aggregate backloads, or product supply with formal grading (6F2, 6F5, Type 1, etc.)",
];

const Aggregates = () => {
  usePageSeo({
    title: "Soil & Hardcore Route Assessment · 3 Counties Recycling",
    description:
      "Assessed routes for suitable soil, stone and hardcore, plus mobile-processing feasibility reviews across Worcestershire, Herefordshire and Gloucestershire.",
    path: "/aggregates",
  });

  return (
    <main data-testid="aggregates-root" className="bg-white text-[#1a1a1a]">
      <Nav />
      <PageHero
        kicker="Aggregates · assessment · feasibility"
        title="Soil and hardcore route assessment, and mobile-project"
        accent="feasibility."
        body="An evidence-led first review of material leaving a site or a proposed mobile-processing project. We identify the information and potential authorised routes needed for a detailed quotation."
        image={AGG_SOIL_SCREENING_URL}
      />

      <section className="bg-white py-16 border-b border-[#024120]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <TrustStrip variant="light" />
        </div>
      </section>

      {/* What we currently assess */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
              / What we can currently do
            </p>
            <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
              Two entry points into the aggregates work.
            </h2>
            <p className="mt-6 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
              Both start with information rather than a truck. The goal is a
              route that stands up to a duty-of-care check, not the cheapest
              option that leaves the risk with you.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {WE_CURRENTLY_ASSESS.map((r) => (
              <article
                key={r.t}
                data-testid={`aggregates-service-${r.t
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-[#F2F2F2] flex flex-col overflow-hidden"
              >
                <div className="relative h-52 md:h-56 bg-[#024120]">
                  <img
                    src={r.img}
                    alt={r.t}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-8 border-l-4 border-[#46B649] flex items-start gap-5 flex-1">
                  <span className="inline-flex h-12 w-12 items-center justify-center bg-[#024120] text-[#46B649] shrink-0">
                    <r.icon className="w-6 h-6" strokeWidth={1.6} />
                  </span>
                  <div>
                    <p className="font-display font-bold text-xl text-[#024120]">
                      {r.t}
                    </p>
                    <p className="mt-2 text-[15px] text-[#4A4A4A] leading-relaxed">
                      {r.d}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Information to send */}
      <section className="bg-[#024120] text-white py-24 md:py-32 grain overflow-hidden relative">
        <div className="max-w-4xl mx-auto px-6 md:px-10 relative">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#FF6600]">
            / What we need from you
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.1]">
            The information that turns an enquiry into a route.
          </h2>
          <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed">
            Send as much of this as you have. If information is missing we
            will tell you exactly what else is needed before we can quote.
          </p>
          <ul className="mt-10 space-y-3">
            {INFO_TO_SEND.map((it, i) => (
              <li
                key={it}
                data-testid={`aggregates-info-${i + 1}`}
                className="flex items-start gap-4 bg-white/5 border-l-4 border-[#46B649] px-5 py-4"
              >
                <span className="font-display font-black text-2xl text-[#46B649] leading-none tabular-nums">
                  0{i + 1}
                </span>
                <span className="text-white/85 text-[15px] leading-relaxed">
                  {it}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Photo banner — finished outcome */}
      <section
        data-testid="aggregates-outcome-banner"
        className="relative bg-[#024120]"
      >
        <div className="relative h-56 md:h-80 overflow-hidden">
          <img
            src={AGG_TRACK_OUTCOME_URL}
            alt="Farm access track laid with suitable graded material"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#024120]/70 via-transparent to-transparent" />
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#46B649]">
            / A properly documented route ends here
          </p>
          <p className="mt-2 text-[#4A4A4A] text-[15px] leading-relaxed">
            Where the material, the source and the receiving route stand up
            to a duty-of-care check, suitable soil, stone and hardcore can
            end up back in useful ground work &mdash; farm tracks, yards
            and hardstandings &mdash; instead of leaving a site as waste.
          </p>
        </div>
      </section>

      {/* Why we start with assessment */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
            / Why we start with assessment
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
            Crushed does not always mean product.
          </h2>
          <p className="mt-6 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            A pile of crushed material is not automatically a certified
            aggregate. Formal classes such as 6F2, 6F5 or Type 1 depend on
            input records, factory production control, sampling, testing,
            end-of-waste evidence and dispatch traceability. Where those are
            not in place, the material still moves as waste and needs the
            correct authorised route. Assessment gets both of those things
            right for the specific job in front of us.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              {
                icon: ClipboardCheck,
                t: "Waste classification",
                d: "Correct EWC/List of Waste code and description agreed before movement.",
              },
              {
                icon: Layers,
                t: "Authorised route",
                d: "A permit, registered exemption or other compliance route matched to the material and activity.",
              },
              {
                icon: ArrowRight,
                t: "Traceability",
                d: "Records that link the load, the description and the receiving site.",
              },
            ].map((c) => (
              <div key={c.t} className="border border-[#024120]/10 p-6">
                <span className="inline-flex h-11 w-11 items-center justify-center bg-[#024120] text-[#46B649]">
                  <c.icon className="w-5 h-5" strokeWidth={1.7} />
                </span>
                <p className="mt-4 font-display font-bold text-[#024120]">
                  {c.t}
                </p>
                <p className="mt-2 text-[14px] text-[#4A4A4A] leading-relaxed">
                  {c.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we do not yet advertise */}
      <section className="bg-[#F2F2F2] py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="flex items-start gap-4 mb-8">
            <span className="inline-flex h-12 w-12 items-center justify-center bg-[#FF6600] text-white shrink-0">
              <AlertTriangle className="w-6 h-6" strokeWidth={1.7} />
            </span>
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
                / What we do not yet advertise
              </p>
              <h2 className="mt-2 font-display font-black text-2xl md:text-3xl tracking-tight text-[#024120] leading-[1.15]">
                Aggregates is a phased service. Some parts are still being
                built.
              </h2>
            </div>
          </div>
          <ul className="space-y-3">
            {NOT_YET_ADVERTISED.map((m) => (
              <li
                key={m}
                className="bg-white border-l-4 border-[#FF6600] px-5 py-4 text-[15px] text-[#024120] leading-relaxed"
              >
                {m}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-[#4A4A4A] leading-relaxed italic">
            These are being scoped alongside the authorisation, production
            control and quality-protocol evidence needed to publish them
            responsibly. Enquire and we will tell you what is currently
            available and what remains in feasibility.
          </p>
        </div>
      </section>

      <CtaBlock
        title="Have material to move, or a mobile-processing project to scope?"
        body="Send site, origin, quantity and any existing analysis or drawings. We will tell you what route is likely to apply and what more we need to price the work."
        primaryLabel="Check a material or project route"
      />

      <Footer />
    </main>
  );
};

export default Aggregates;
